import { ExternalLink, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { TicketDetailIssueView } from "@sd/components/tickets/TicketDetailIssueView";
import { useAuth } from "@sd/hooks/useAuth";
import { useTicketDetailPage } from "@sd/hooks/useTicketDetailPage";
import { buildTicketDetailPath } from "@sd/lib/ticketDetailPath";

export function TicketDetailDrawer({
  ticketId,
  onClose,
  returnTo,
  fallbackLabel,
}: {
  ticketId: string | null;
  onClose: () => void;
  returnTo?: string;
  fallbackLabel?: string;
}) {
  const navigate = useNavigate();
  const { role } = useAuth();
  const [mounted, setMounted] = useState(false);
  const id = ticketId?.trim() ?? "";
  const {
    ticket,
    display,
    attachments,
    attachmentsLoading,
    activityEntries,
    activityPending,
    activityError,
    notes,
    notesPending,
    notesError,
    resolveUserLabel,
    isLoading,
    isError,
  } = useTicketDetailPage(id || undefined);

  useEffect(() => {
    setMounted(true);
  }, []);

  const title = ticket?.uiId ?? ticket?.id ?? fallbackLabel ?? "Ticket details";

  const drawer = (
    <Sheet open={Boolean(id)} onOpenChange={open => !open && onClose()}>
      <SheetContent
        side="right"
        overlayClassName="z-[100]"
        className="z-[100] flex h-full w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-xl md:max-w-2xl"
      >
        <SheetHeader className="shrink-0 space-y-2 border-b px-4 py-3 pr-12 text-left">
          <SheetTitle className="font-mono text-base">{title}</SheetTitle>
          <SheetDescription className="sr-only">
            Ticket details preview. Close the drawer to return to the board.
          </SheetDescription>
          {id ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-fit gap-1.5"
              onClick={() => navigate(buildTicketDetailPath(id, { returnTo }))}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Open full page
            </Button>
          ) : null}
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : isError || !ticket ? (
            <p className="p-4 text-sm text-destructive">
              Could not load ticket details. The ticket may not exist or you may
              not have access.
            </p>
          ) : (
            <TicketDetailIssueView
              ticket={ticket}
              display={display}
              activityEntries={activityEntries}
              activityPending={activityPending}
              activityError={activityError}
              notes={notes}
              notesPending={notesPending}
              notesError={notesError}
              attachments={attachments}
              attachmentsLoading={attachmentsLoading}
              resolveUserLabel={resolveUserLabel}
              showAddNoteSection={role === "ServiceDesk_Admin"}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );

  if (!mounted) return null;
  return createPortal(drawer, document.body);
}
